import type {
  CreateNotificationData,
  CreateNotificationResponse,
  MarkAllAsReadResponse,
  MarkAsReadData,
  Notification,
  NotificationsQuery,
  NotificationsResponse,
  SendEmailData,
  SendEmailResponse,
  SendPushNotificationData,
  SendPushNotificationResponse,
} from '@/types/notifications';

import { getStoredToken } from './apiClient';
import { ApiError, authenticatedRequest } from './base.service';

export const notificationsService = {
  async getNotifications(query: NotificationsQuery = {}): Promise<NotificationsResponse> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    const searchParams = new URLSearchParams();
    if (query.page) searchParams.append('page', query.page.toString());
    if (query.limit) searchParams.append('limit', query.limit.toString());
    if (query.orderDirection) searchParams.append('orderDirection', query.orderDirection);
    if (query.userId) searchParams.append('userId', query.userId);
    if (query.organizationId) searchParams.append('organizationId', query.organizationId);
    if (query.isRead !== undefined) searchParams.append('isRead', query.isRead.toString());
    if (query.type) searchParams.append('type', query.type);

    const queryString = searchParams.toString();
    const url = `/api/v1/notifications${queryString ? `?${queryString}` : ''}`;

    return authenticatedRequest<NotificationsResponse>(url, {}, token);
  },

  async getNotification(notificationId: string): Promise<Notification> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);
    return authenticatedRequest<Notification>(`/api/v1/notifications/${notificationId}`, {}, token);
  },

  async createNotification(data: CreateNotificationData): Promise<CreateNotificationResponse> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);
    return authenticatedRequest<CreateNotificationResponse>(
      '/api/v1/notifications',
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      token
    );
  },

  async markAsRead(notificationId: string, data: MarkAsReadData): Promise<Notification> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);
    return authenticatedRequest<Notification>(
      `/api/v1/notifications/${notificationId}/read`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
      token
    );
  },

  async markAllAsRead(): Promise<MarkAllAsReadResponse> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);
    return authenticatedRequest<MarkAllAsReadResponse>(
      '/api/v1/notifications/mark-all-read',
      {
        method: 'PATCH',
      },
      token
    );
  },

  async deleteNotification(notificationId: string): Promise<void> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);
    return authenticatedRequest<void>(
      `/api/v1/notifications/${notificationId}`,
      {
        method: 'DELETE',
      },
      token
    );
  },

  async sendPushNotification(
    data: SendPushNotificationData
  ): Promise<SendPushNotificationResponse> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);
    return authenticatedRequest<SendPushNotificationResponse>(
      '/api/v1/notifications/send-push-notification',
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      token
    );
  },

  async sendEmail(data: SendEmailData): Promise<SendEmailResponse> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);
    return authenticatedRequest<SendEmailResponse>(
      '/api/v1/notifications/send-email',
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      token
    );
  },
};
